import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import WarningIcon from '@material-ui/icons/Warning';
import {
  initiateClose as initiateCloseAction,
  closed as closedAction
} from '../../features/notificationSystem/actions';
import {
  getNotification,
  getIsOpen
} from '../../features/notificationSystem/selectors';
import ImmutablePropsToJS from '../../utils/ImmutablePropsToJS';

const variantIcon = {
  success: CheckCircleIcon,
  warning: WarningIcon,
  error: ErrorIcon,
  info: InfoIcon
};

const useStyles = makeStyles(theme => ({
  success: {
    backgroundColor: theme.palette.success.main
  },
  error: {
    backgroundColor: theme.palette.error.main
  },
  info: {
    backgroundColor: theme.palette.information.main
  },
  warning: {
    backgroundColor: theme.palette.warning.main
  },
  icon: {
    fontSize: 20
  },
  iconVariant: {
    opacity: 0.9,
    marginRight: theme.spacing(1)
  },
  message: {
    display: 'flex',
    alignItems: 'center'
  }
}));

const Notification = ({
  notification = null,
  isOpen,
  initiateClose,
  closed
}) => {
  const classes = useStyles();

  if (notification === null) {
    return null;
  }

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    initiateClose();
  };

  const handleExited = () => {
    closed();
  };

  function getAction() {
    const action = [];
    if (notification.showClose) {
      action.push(
        <IconButton
          key="close"
          aria-label="close"
          color="inherit"
          onClick={handleClose}
        >
          <CloseIcon className={classes.icon} />
        </IconButton>
      );
    }
    return action;
  }

  const { message, duration, variant = 'info', anchorOrigin } = notification;
  const action = getAction();
  const Icon = variantIcon[variant];

  return (
    <Snackbar
      open={isOpen}
      onClose={handleClose}
      onExited={handleExited}
      anchorOrigin={anchorOrigin}
      duration={duration}
    >
      <SnackbarContent
        className={classes[variant]}
        aria-describedby="client-snackbar"
        message={
          <span id="client-snackbar" className={classes.message}>
            <Icon className={cx(classes.icon, classes.iconVariant)} />
            {message}
          </span>
        }
        action={action}
      />
    </Snackbar>
  );
};

Notification.propTypes = {
  notification: PropTypes.object,
  isOpen: PropTypes.bool,
  initiateClose: PropTypes.func.isRequired,
  closed: PropTypes.func.isRequired
};

export default connect(
  state => ({
    notification: getNotification(state),
    isOpen: getIsOpen(state)
  }),
  { initiateClose: initiateCloseAction, closed: closedAction }
)(ImmutablePropsToJS(Notification));
